import { useEffect, useState } from 'react';
import { Trans, type Lang } from './i18n/Trans';
import { ApiHelper } from './helper/ApiHelper';
import { LoginPage, type PublicUser } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { QuickQuotePage } from './pages/QuickQuotePage';
import { KicadPluginPage } from './pages/KicadPluginPage';
import { OrdersPage } from './pages/OrdersPage';
import { SettingsPage } from './pages/SettingsPage';
import { Modal } from './components/Modal';
import logoUrl from './assets/logo.png';
import { SiteConfig } from './config/site';

/**
 * 客户用的两个大 tab。**订单与系统设置不在这里** —— 它们是"我的/内部"的东西，
 * 放右上角导航。这平台是拉客户来打样采购的，主区就该只有"传 BOM → 看报价 → 下单"这条路，
 * 别让加价率、订单管理这类自家后台的东西挡在客户面前。
 */
type TabKey = 'quote' | 'kicad';

/** 右上角进去的内部区（null = 正常首页） */
type Zone = 'orders' | 'settings' | null;
/** 登录/注册是**弹窗**不是页面：跳转整页会把用户手上的活（刚跑完的报价单、正要下的单）冲掉 */
type AuthModal = 'login' | 'register' | null;
type Theme = 'light' | 'dark';

/**
 * 壳。**首页不挡人**：没登录也能进、能看、能试算——这是对外系统的硬要求。
 * 登录只在"拿走东西/改设置"时才要（下载报价单、改加价率），且右上角常驻登录/注册入口。
 * 品牌头与 hero 的观感与 auto_design 平台同源（同一家公司的两个站点）。
 */
export function App() {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [auth, setAuth] = useState<AuthModal>(null);
  // 登录前被打断的那个动作：登录成功后自动接着做（比如继续弹下单框）
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [tab, setTab] = useState<TabKey>('quote');
  const [zone, setZone] = useState<Zone>(null);
  const [theme, setTheme] = useState<Theme>(
    document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
  );
  const [menuOpen, setMenuOpen] = useState(false); // 窄屏导航折叠
  const isAdmin = user?.role === 'admin';
  const canManageOrders = user?.role === 'admin' || user?.role === 'buyer';
  const [, setLang] = useState<Lang>(Trans.current());

  // 有 token 就顺手认一下人；没有/过期都不影响继续用
  useEffect(() => {
    if (!ApiHelper.token()) return;
    ApiHelper.call<{ user: PublicUser }>('/api/auth/me')
      .then((r) => setUser(r.user))
      .catch(() => ApiHelper.setToken(''));
  }, []);

  const switchTheme = () => {
    const next: Theme = theme === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('hip.theme', next);
    setTheme(next);
  };

  const logout = async () => {
    await ApiHelper.call('/api/auth/logout', 'POST').catch(() => undefined);
    ApiHelper.setToken('');
    setUser(null);
    setTab('quote');
    setZone(null);
  };

  const onAuthed = (u: PublicUser) => {
    setUser(u);
    setAuth(null);
    // 关键：把登录前那个动作接上，用户不用从头再来
    if (pending) { const go = pending; setPending(null); setTimeout(go, 0); }
  };

  /** 子页面要求登录：开弹窗，并记下登录完要继续做什么 */
  const needLogin = (after?: () => void) => {
    setPending(after ?? null);
    setAuth('login');
  };

  const closeAuth = () => { setAuth(null); setPending(null); };

  return (
    <>
      <header className="app-header">
        <div className="brand" onClick={() => { closeAuth(); setTab('quote'); setZone(null); setMenuOpen(false); }}>
          <span className="brand-mark" aria-hidden="true">
            <img src={logoUrl} alt="" />
          </span>
          <span className="brand-text">
            <span className="brand-title">
              <span className="brand-name">{Trans.t('app.title')}</span>
              <span className="brand-full-name">{Trans.t('app.fullName')}</span>
            </span>
            <span className="brand-backing">
              <span className="brand-hkex"><HKFlag />{Trans.t('app.hkex')}</span>
              <span className="brand-company">{Trans.t('app.company')}</span>
              <span className="brand-stock">
                {Trans.t('app.stockLabel')} <b>{Trans.t('app.stockCode')}</b>
              </span>
            </span>
          </span>
        </div>
        <div className="spacer" />
        <button className="btn ghost nav-toggle" aria-label="menu" onClick={() => setMenuOpen((v) => !v)}>
          {menuOpen ? '×' : '☰'}
        </button>
        <nav className={'nav-cluster' + (menuOpen ? ' open' : '')}>
          <button className="btn ghost" onClick={switchTheme}>
            {Trans.t(theme === 'light' ? 'theme.toDark' : 'theme.toLight')}
          </button>
          <button className="btn ghost" onClick={() => setLang(Trans.toggle())}>
            {Trans.t('lang.switch')}
          </button>
          {user ? (
            <>
              {/* 订单：谁都能看自己的；管理员看全部（后端按 order.manage 决定） */}
              <button
                className={'btn ghost' + (zone === 'orders' ? ' on' : '')}
                onClick={() => { setZone('orders'); setMenuOpen(false); }}
              >
                {Trans.t(canManageOrders ? 'nav.ordersAdmin' : 'nav.orders')}
              </button>
              {/* 系统设置：只有管理员看得到这个入口（加价率这类内部参数在里面） */}
              {isAdmin && (
                <button
                  className={'btn ghost' + (zone === 'settings' ? ' on' : '')}
                  onClick={() => { setZone('settings'); setMenuOpen(false); }}
                >
                  {Trans.t('nav.settings')}
                </button>
              )}
              <span className="app-user">{user.name} · {user.role}</span>
              <button className="btn ghost" onClick={logout}>{Trans.t('login.logout')}</button>
            </>
          ) : (
            <>
              <button className="btn ghost" onClick={() => { setAuth('login'); setMenuOpen(false); }}>
                {Trans.t('login.submit')}
              </button>
              <button className="btn accent" onClick={() => { setAuth('register'); setMenuOpen(false); }}>
                {Trans.t('register.submit')}
              </button>
            </>
          )}
        </nav>
      </header>
      {menuOpen && <div className="nav-scrim" onClick={() => setMenuOpen(false)} />}

      {/* 登录/注册是弹窗：底下的首页不卸载，报价单与正在填的东西都还在 */}
      {auth === 'login' && (
        <Modal title={Trans.t('login.title')} onClose={closeAuth}>
          {pending && <p className="hint">{Trans.t('login.needFirst')}</p>}
          <LoginPage
            embedded
            onLogin={onAuthed}
            onGoRegister={() => setAuth('register')}
            onCancel={closeAuth}
          />
        </Modal>
      )}
      {auth === 'register' && (
        <Modal title={Trans.t('register.title')} onClose={closeAuth}>
          <RegisterPage embedded onDone={onAuthed} onGoLogin={() => setAuth('login')} onCancel={closeAuth} />
        </Modal>
      )}

      <div className="container">
        {zone === null ? (
          <>
            <nav className="tabbar">
              <button className={'tab' + (tab === 'quote' ? ' active' : '')} onClick={() => setTab('quote')}>
                {Trans.t('tab.quickQuote')}
              </button>
              <button className={'tab' + (tab === 'kicad' ? ' active' : '')} onClick={() => setTab('kicad')}>
                {Trans.t('tab.kicadPlugin')}
              </button>
            </nav>
            <main className="panel">
              {tab === 'quote' && <QuickQuotePage user={user} onNeedLogin={needLogin} />}
              {tab === 'kicad' && <KicadPluginPage />}
            </main>
          </>
        ) : (
          <>
            <nav className="tabbar">
              <button className="tab" onClick={() => setZone(null)}>← {Trans.t('nav.back')}</button>
            </nav>
            <main className="panel">
              {zone === 'orders' && user && <OrdersPage canSeeCost={canManageOrders} />}
              {zone === 'settings' && isAdmin && <SettingsPage />}
            </main>
          </>
        )}
      </div>

      {/* 底部备案信息：ICP 备案号是监管硬要求，且必须与工信部登记的一字不差 */}
      <footer className="app-footer">
        <span className="app-footer-copy">© {Trans.t('app.company')}</span>
        {SiteConfig.icp && (
          <a className="app-footer-icp" href={SiteConfig.icpLink} target="_blank" rel="noreferrer">
            {SiteConfig.icp}
          </a>
        )}
      </footer>
    </>
  );
}

/** 港区旗：紫荆五瓣 + 圆心。用 SVG 而不是 emoji——Windows 上 emoji 旗帜不显示。 */
function HKFlag() {
  const cx = 10, cy = 7;
  return (
    <svg className="hk-flag" viewBox="0 0 20 14" width="18" height="13" aria-hidden="true">
      <rect x="0" y="0" width="20" height="14" rx="2.5" fill="#DE2910" />
      {[0, 72, 144, 216, 288].map((a) => (
        <ellipse key={a} cx={cx} cy={cy - 4} rx="1.15" ry="3.1" fill="#fff"
          transform={`rotate(${a} ${cx} ${cy})`} />
      ))}
      <circle cx={cx} cy={cy} r="1.1" fill="#DE2910" />
    </svg>
  );
}
